import { ComparableBoatData, useGetActiveCarouselItem } from '@components/CompareBoats/hooks/useCompare';
import { Button, Grid, Tooltip } from 'antd';
import { KeenSliderPlugin, useKeenSlider } from 'keen-slider/react';
import { Info } from 'lucide-react';
import { FC, useEffect } from 'react';
import styles from '../CompareTable.module.scss';
import PanelDataItem from './PanelDataItem.component';

interface RigSpecsPanelDataInterface {
  boatComparableData: ComparableBoatData[];
}

const MutationPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      slider.update();
    });
  });

  const config = { childList: true };

  slider.on('created', () => {
    observer.observe(slider.container, config);
  });
  slider.on('destroyed', () => {
    observer.disconnect();
  });
};

const RigSpecsPanelData: FC<RigSpecsPanelDataInterface> = ({ boatComparableData }) => {
  const activeCarouselItem = useGetActiveCarouselItem();

  const { md } = Grid.useBreakpoint();

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      drag: false,
      slides: {
        perView: 7,
      },
      breakpoints: {
        '(max-width: 1440px)': {
          slides: { perView: 6 },
        },
        '(max-width: 1200px)': {
          slides: { perView: 5 },
        },
        '(max-width: 1024px)': {
          slides: { perView: 4 },
        },
        '(max-width: 992px)': {
          slides: { perView: 3 },
        },
        '(max-width: 768px)': {
          slides: { perView: 2 },
        },
        '(max-width: 520px)': {
          slides: { perView: 2 },
        },
      },
    },
    [MutationPlugin],
  );

  useEffect(() => {
    instanceRef?.current?.moveToIdx(activeCarouselItem);
  }, [activeCarouselItem, instanceRef]);

  const rigSpecsList: { specLabel: string; specId: number; noDataSpec?: boolean; specTooltip?: string }[] = [
    { specLabel: 'Rigging', specId: 56 },
    { specLabel: 'Rotating spars', specId: 57 },
    { specLabel: 'Mast', specId: 58 },
    { specLabel: 'Spars', specId: 59 },
    { specLabel: 'Standing rigging', specId: 60 },
    { specLabel: 'Number of spreaders', specId: 61 },
    { specLabel: 'Spreaders angle', specId: 62 },
    { specLabel: 'Upwind sail area', specId: 63 },
    { specLabel: 'Downwind sail area', specId: 64 },
    { specLabel: 'Mainsail', specId: 65, noDataSpec: true },
    { specLabel: 'Mainsail A', specId: 66 },
    { specLabel: 'Headsail', specId: 67, noDataSpec: true },
    { specLabel: 'Headsail A', specId: 68 },
    { specLabel: 'Spinnaker', specId: 69, noDataSpec: true },
    { specLabel: 'Spinnaker A', specId: 70 },
  ];

  return (
    <div className={styles.specsPanelDataContainer}>
      <div style={md ? { width: 192 } : { width: '100%' }}>
        {rigSpecsList.map((mainSpecItem) => (
          <div key={mainSpecItem.specId}>
            <div className={mainSpecItem?.noDataSpec ? styles.noDataSpecsNamesStyles : styles.specsNamesStyles}>
              {mainSpecItem.specLabel}
              {mainSpecItem?.specTooltip && (
                <Tooltip title={mainSpecItem?.specTooltip}>
                  <Button icon={<Info size={16} />} shape="circle" className={styles.tooltipInfoButton} />
                </Tooltip>
              )}
            </div>

            {mainSpecItem?.specId === 56 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.rigging} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 57 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.rotatingSpars} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 58 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.mastConfiguration} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 59 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.spars} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 60 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.standingRigging} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 61 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.spreadersLevel} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 62 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.spreadersAngle} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 63 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.upwindSailArea} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 64 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.downwindSailArea} panelDataType="text" key={boat?._id} />
              ))}

            {mainSpecItem?.specId === 66 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.mainsail?.at(0)?.name} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 68 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.headsail?.at(0)?.name} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 70 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.spinnaker?.at(0)?.name} panelDataType="text" key={boat?._id} />
              ))}
          </div>
        ))}
      </div>

      {md && (
        <div ref={ref} className="keen-slider">
          {boatComparableData?.map((boat) => (
            <div key={boat._id} className="keen-slider__slide">
              <PanelDataItem panelData={boat?.rigging} panelDataType="text" />
              <PanelDataItem panelData={boat?.rotatingSpars} panelDataType="text" />
              <PanelDataItem panelData={boat?.mastConfiguration} panelDataType="text" />
              <PanelDataItem panelData={boat?.spars} panelDataType="text" />
              <PanelDataItem panelData={boat?.standingRigging} panelDataType="text" />
              <PanelDataItem panelData={boat?.spreadersLevel} panelDataType="text" />
              <PanelDataItem panelData={boat?.spreadersAngle} panelDataType="text" />
              <PanelDataItem panelData={boat?.upwindSailArea} panelDataType="text" />
              <PanelDataItem panelData={boat?.downwindSailArea} panelDataType="text" />
              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.mainsail?.at(0)?.name} panelDataType="text" />
              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.headsail?.at(0)?.name} panelDataType="text" />
              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.spinnaker?.at(0)?.name} panelDataType="text" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RigSpecsPanelData;
